import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
  RequiredFieldsText,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import {
  getLearnerTerm,
  Product,
} from "@transfr-inc/dashboard-components/constants";
import {
  Button,
  Input,
  ToggleButton,
} from "@transfr-inc/dashboard-components/forms";

import { hasAnyProducts, resolveCurrentProduct } from "@lib/product.utils";
import { useContainer } from "@contexts/container-context";
import { useClassroomInstructors, useFeatures, useIsIntegrationOrg } from "@queries";
import { useCourseBuilderEnabled } from "@hooks/queries/account-selectors";
import { UserOptionItem } from "@views/classrooms/creation/user-option-item";
import {
  BUILD_YOUR_OWN_PROGRAM,
  getProductIdsForClassroomCreation,
  useClassroomCreationData,
} from "@views/classrooms/creation/new-classroom.utils";
import { RemoveInstructorModal } from "@views/classrooms/details/modals/remove-instructor.modal";
import {
  ProductDropDownList,
  ProfessionalLearningProduct,
  TrekTrainProduct,
} from "@components/custom/dropdowns/product-dropdown-list";
import { ProgramDropDownList } from "@components/custom/dropdowns/program-dropdown-list";

import { AddInstructorsSection } from "../components/add-instructors.section";

import "./edit-classroom.modal.scss";

const getInstructorsDiff = (instructors, previousInstructors) => {
  const instructorsSet = new Set(
    instructors.map((instructor) => instructor.userId)
  );
  const previousInstructorsSet = new Set(
    previousInstructors.map((instructor) => instructor.userId)
  );
  const toAdd = instructors.filter(
    (instructor) => !previousInstructorsSet.has(instructor.userId)
  );
  const toRemove = previousInstructors.filter(
    (instructor) => !instructorsSet.has(instructor.userId)
  );
  return { toAdd, toRemove };
};

function productSupportsProgram(product) {
  return (
    product?.id === Product.PROG ||
    product?.id === ProfessionalLearningProduct.id
  );
}

export function EditClassroomModal({
  classroom,
  open,
  onEditClassroom,
  onCancel,
}) {
  const [loading, setLoading] = useState();
  const [instructorsToRemove, setInstructorsToRemove] = useState();
  const [notification, setNotification] = useState();
  const { instructorCreationEnabled } = useFeatures();

  const { courseService } = useContainer();
  const { data: instructors } = useClassroomInstructors(classroom?.classroomId);
  const {
    availableProducts,
    programs,
    isLoading: programsLoading,
  } = useClassroomCreationData(classroom?.clientCode, false);
  const courseBuilderEnabled = useCourseBuilderEnabled();
  const { isIntegrationOrg } = useIsIntegrationOrg(classroom?.clientCode);

  const isBasics = classroom?.isProfessionalLearning;

  const currentProduct = useMemo(
    () => resolveCurrentProduct(classroom),
    [classroom]
  );

  // Fetch the current program directly. If the course has the same source_classroom_id
  // as the classroom's ID
  // it's a "Build Your Own" course — represent it with the BUILD_YOUR_OWN_PROGRAM
  // sentinel so the dropdown shows the right option.
  const [currentProgram, setCurrentProgram] = useState(null);
  useEffect(() => {
    if (!classroom?.courseId) return;
    courseService
      .getCourseDetails(classroom.courseId)
      .then((course) =>
        setCurrentProgram(
          course?.sourceClassroomId == classroom?.classroomId
            ? BUILD_YOUR_OWN_PROGRAM
            : course
        )
      )
      .catch(() => {});
  }, [classroom?.courseId]);

  // Filter Basics out of available products for non-Basics classrooms.
  // Basics classrooms lock the product entirely (can't switch away).
  const editableProducts = useMemo(() => {
    if (isBasics) return availableProducts ?? [];
    return (availableProducts ?? []).filter(
      (p) =>
        p !== ProfessionalLearningProduct &&
        p?.id !== ProfessionalLearningProduct.id
    );
  }, [availableProducts, isBasics]);

  const defaultValues = useMemo(
    () => ({
      ...classroom,
      ceseStudentNotifications: classroom.ceseStudentNotifications ?? true,
      instructors: instructors?.map((i) => new UserOptionItem(i)),
      product: currentProduct,
      program: currentProgram,
    }),
    [classroom, instructors, currentProduct, currentProgram]
  );

  const methods = useForm({
    criteriaMode: "all",
    mode: "onBlur",
    defaultValues,
  });
  const {
    formState,
    getValues,
    control,
    reset,
    handleSubmit,
    setValue,
    trigger,
  } = methods;

  const selectedProduct = useWatch({ control, name: "product" });

  const resetForm = useCallback(() => {
    reset(defaultValues, { keepDefaultValues: true });
  }, [reset, defaultValues]);

  useEffect(() => {
    if (open && currentProgram) {
      setValue("program", currentProgram, { shouldValidate: true });
    }
  }, [currentProgram]);

  const getFormData = useCallback(() => {
    const {
      product,
      program,
      instructors: formInstructors,
      ...rest
    } = getValues();
    const instructorsDiff = getInstructorsDiff(
      formInstructors.map((i) => i.data),
      instructors
    );
    const isBuildYourOwn =
      productSupportsProgram(product) &&
      program?.id === BUILD_YOUR_OWN_PROGRAM.id;
    return {
      ...rest,
      instructors: instructorsDiff,
      product_ids: getProductIdsForClassroomCreation(product),
      // Always send program_id so the backend knows whether to link/unlink.
      // null tells the backend to remove any existing program link.
      program_id:
        productSupportsProgram(product) && !isBuildYourOwn
          ? program?.id ?? null
          : null,
      build_your_own_course: isBuildYourOwn,
    };
  }, [getValues, instructors]);

  const onCloseHandler = () => {
    onCancel && onCancel();
    setNotification();
    resetForm();
  };

  const onEditClassroomHandler = async (data) => {
    setNotification();
    setLoading(true);
    try {
      await onEditClassroom(data);
    } catch {
      setNotification({
        type: NotificationType.error,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onApplyChangesHandler = async () => {
    const data = getFormData();

    if (data.instructors.toRemove.length > 0) {
      setInstructorsToRemove(data.instructors.toRemove);
    } else {
      await onEditClassroomHandler(data);
    }
  };

  const onInstructorsConfirmedHandler = async () => {
    setInstructorsToRemove();
    const data = getFormData();
    await onEditClassroomHandler(data);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  return (
    <>
      <Modal
        modalClassName={"edit-classroom-modal"}
        open={open}
        onClose={onCloseHandler}
        preventClose={loading}
      >
        {loading && <Loader overlay></Loader>}
        <ModalHeader
          className="blue-icon"
          icon={["fa-regular", "pen-to-square"]}
          title="Edit Classroom Details"
        ></ModalHeader>
        <ModalBody>
          {notification && (
            <Notification {...notification}>
              {notification.message}
            </Notification>
          )}
          <RequiredFieldsText />
          <FormProvider {...methods}>
            <form
              className="edit-trainee-form"
              noValidate={true}
              onSubmit={handleSubmit(onEditClassroomHandler)}
            >
              <Controller
                control={control}
                name="name"
                defaultValue={classroom?.name}
                rules={{
                  required: true,
                  maxLength: 50,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={
                      <>
                        Name <sup>*</sup>
                      </>
                    }
                    type="text"
                    errors={formState.errors?.name}
                    required
                  />
                )}
              />
              {!isIntegrationOrg && (
                <div className="classroom-product">
                  <div className="section-title">
                    Product <sup>*</sup>
                  </div>
                  <Controller
                    control={control}
                    name="product"
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <ProductDropDownList
                        products={availableProducts.filter(
                          (p) => p?.id !== ProfessionalLearningProduct.id
                        )}
                        selectedProduct={value}
                        onProductSelected={(product) => {
                          onChange(product);
                          if (!productSupportsProgram(product)) {
                            setValue("program", null);
                          }
                        }}
                        className="org-dropdown"
                        combineCeTrek
                        disabled={isBasics}
                      />
                    )}
                  />
                </div>
              )}
              {!isIntegrationOrg &&
                productSupportsProgram(selectedProduct) && (
                  <div className="classroom-program">
                    <div className="section-title">
                      Program <sup>*</sup>
                    </div>
                    <Controller
                      control={control}
                      name="program"
                      rules={{
                        validate: (value) =>
                          !productSupportsProgram(selectedProduct) ||
                          !!value ||
                          "Program is required",
                      }}
                      render={({ field: { value, onChange } }) => {
                        const basePrograms = isBasics
                          ? programs?.filter((p) => p.professionalLearning)
                          : programs?.filter((p) => !p.professionalLearning) ??
                            [];
                        const programOptions =
                          !isBasics && courseBuilderEnabled
                            ? [BUILD_YOUR_OWN_PROGRAM, ...basePrograms]
                            : basePrograms;
                        return (
                          <ProgramDropDownList
                            key={value?.id ?? "no-program"}
                            programs={programOptions}
                            defaultProgram={value}
                            onProgramSelected={(v) => {
                              onChange(v);
                              trigger("program");
                            }}
                            className="org-dropdown"
                          />
                        );
                      }}
                    />
                  </div>
                )}
              {hasAnyProducts(
                [selectedProduct?.id],
                [Product.CE_TRK, TrekTrainProduct.id]
              ) && (
                <Controller
                  control={control}
                  name="ceseStudentNotifications"
                  defaultValue={true}
                  render={({
                    field: {
                      // ToggleButton is a functional component without
                      // forwardRef, so we don't pass the ref to avoid warnings.
                      // If ToggleButton is updated to use forwardRef, we can
                      // remove this.
                      ref,
                      ...field
                    },
                  }) => (
                    <div className="toggle-container">
                      <h6 className="toggle-label">
                        TREK STUDENT REMINDERS
                        <TextTooltip
                          text={`Email Trek reminders to your ${getLearnerTerm({
                            lowercase: true,
                            plural: true,
                          })}`}
                        >
                          <FontAwesomeIcon icon={"fa-regular fa-circle-info"} />
                        </TextTooltip>
                      </h6>
                      <ToggleButton
                        {...field}
                        affirmText="ON"
                        negateText="OFF"
                      />
                    </div>
                  )}
                />
              )}
              <AddInstructorsSection
                classroom={classroom}
                creationEnabled={instructorCreationEnabled}
              />
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onCloseHandler}>Cancel</Button>
          <Button
            primary
            onClick={onApplyChangesHandler}
            disabled={!formState.isValid || programsLoading}
          >
            Done
          </Button>
        </ModalFooter>
      </Modal>
      <RemoveInstructorModal
        open={instructorsToRemove}
        classroom={classroom}
        onCancel={() => setInstructorsToRemove()}
        onRemoveInstructor={onInstructorsConfirmedHandler}
        instructors={instructorsToRemove}
      />
    </>
  );
}
